import { Injectable, inject } from '@angular/core';
import { NotyfNotification } from 'notyf';
import { ToasterService } from './toaster.service';

/**
 * Dedicated toast API for settings screens (LUNA-26), so that code doesn't reach into the
 * generic, app-wide `ToasterService` directly. Deliberately wraps — does NOT construct its own
 * `Notyf` instance: `Notyf` only accepts its type presets at construction time, so a second
 * instance would mean a second, uncoordinated stacking/positioning container on top of the one
 * every other toast in the app already uses. Uses `ToasterService.openCustom()`/`dismiss()` (not
 * `show()`/`showPageLoading()`) so that this service's own active/loading-notification tracking
 * is entirely independent of every *other* consumer's (kanban, scheduler's diary mutations, etc.)
 * — a settings toast never dismisses someone else's toast, or vice versa, even though they all
 * render through the same shared Notyf instance/DOM container.
 *
 * IMPORTANT: this only works because both services live in this same bundle (`cs-componest-ng20`)
 * and share the same Angular injector. If this class is ever moved into a different bundle (e.g.
 * `settings-shared`, which is compiled into each settings MFE's own separate bootstrap), injecting
 * `ToasterService` there would silently construct a second, disconnected instance instead of
 * reusing this one — with no compile error to catch it. Keep this here; expose it to other
 * bundles only via the `MFE_REGISTRY` bridge, the same way `settingsSidePanel` is.
 *
 * Also note: `window.MFE_REGISTRY.settingsToaster` is a plain global, reachable from any JS on
 * the page (legacy v4/AngularJS code included) — nothing technically restricts it to settings
 * screens, that's convention only, same as every other entry on the registry.
 */
@Injectable({ providedIn: 'root' })
export class SettingsToasterService {
  private readonly toaster = inject(ToasterService);

  private activeNotification?: NotyfNotification;
  private loadingNotification?: NotyfNotification;
  private pageLoadingCount = 0;

  show(message: string, options: { type?: 'success' | 'error' } = {}): NotyfNotification {
    if (this.activeNotification) {
      this.toaster.dismiss(this.activeNotification);
    }
    // Symmetric with showLoading()'s own dismissal below — a lingering settings loading toast
    // and a following settings success/error toast shouldn't coexist.
    if (this.loadingNotification) {
      this.toaster.dismiss(this.loadingNotification);
      this.loadingNotification = undefined;
      this.pageLoadingCount = 0;
    }

    const notification = this.toaster.openCustom({ ...options, message });
    this.activeNotification = notification;
    return notification;
  }

  /** Persistent top toast while a settings save is in flight (ref-counted, settings-only —
   *  independent of ToasterService.showPageLoading()'s own ref-count used by other consumers). */
  showLoading(message: string): void {
    this.pageLoadingCount += 1;
    if (this.pageLoadingCount > 1) {
      return;
    }

    if (this.activeNotification) {
      this.toaster.dismiss(this.activeNotification);
      this.activeNotification = undefined;
    }
    if (this.loadingNotification) {
      this.toaster.dismiss(this.loadingNotification);
      this.loadingNotification = undefined;
    }

    this.loadingNotification = this.toaster.openCustom({
      type: 'loading',
      message,
      duration: 0,
      dismissible: false,
    });
  }

  hideLoading(): void {
    if (this.pageLoadingCount <= 0) {
      return;
    }
    this.pageLoadingCount -= 1;
    if (this.pageLoadingCount > 0) {
      return;
    }

    if (this.loadingNotification) {
      this.toaster.dismiss(this.loadingNotification);
      this.loadingNotification = undefined;
    }
  }
}
